<script lang="ts">

    import 'mapbox-gl/dist/mapbox-gl.css';
    import { AnimationOptions, EaseToOptions, FitBoundsOptions, LngLat, LngLatBounds, LngLatBoundsLike, Map as Mapbox } from 'mapbox-gl';
    import { createEventDispatcher, onMount, setContext } from 'svelte';
    import type { CoordinatesDto, Fn, TargetDto } from '../../global';
    import { userBearing } from '../../Services/compass';
    import { derived } from 'svelte/store';
    import { calcBearing, playerPosition } from '../../Services/geolocation';
    import { currentTarget } from '../../Services/game';
    import { followMode }    from '../../Services/follow';

    const dispatch = createEventDispatcher();
    const followingData = derived([playerPosition, currentTarget, userBearing], ([playerPosition, currentTarget, userBearing]) => {
        return [playerPosition, currentTarget, userBearing];
    });

    let followModeEnabled = false;
    let mapContainer: HTMLElement;
    let map: Mapbox;
    let mapReady = false;
    let unsubFollowingData: Fn;

    $: toggleFollowMode(map, $followMode);

    setContext('getMapbox', () => map);

    onMount(() => {

        map = new Mapbox({
            container: mapContainer,
            accessToken: 'pk.eyJ1Ijoic2ltb25lY2hlaWRlIiwiYSI6ImNqeGx5NmU5bjBhZGczdG50N2t4c20yangifQ.1VoPfDI1X6rAW8iYn4x5fA',
            // style: 'mapbox://styles/felipelorenzo/ck67n4apy0duz1imgca15xepj',
            style: 'mapbox://styles/felipelorenzo/ck9khgigq14ay1inychjvw25n',
            attributionControl: false,
            logoPosition: 'bottom-right',
            center: [-16.58398605243506, 28.385479983806547],
            zoom: 13
        });

        map.on('styledata', () => mapReady = true);
        map.on('movestart', () => dispatch('movestart'));
        map.on('rotate', () => dispatch('rotate', map.getBearing()));

    });

    export function getCenter(): LngLat {
        return map.getCenter();
    }

    export function easeTo(options: EaseToOptions): Promise<void> {
        return new Promise(resolve => {
            const eventId = Symbol();
            const onMoveend = (event: any) => {
                if (event.eventId !== eventId) {
                    return;
                }
                map.off('moveend', onMoveend);
                resolve();
            };
            map.on('moveend', onMoveend);
            map.easeTo(options);
        });
    }

    export function getZoom(): number {
        return map.getZoom();
    }

    export function getBounds(): LngLatBounds {
        return map.getBounds();
    }

    export function panTo(coordinates: CoordinatesDto, options?: AnimationOptions): Promise<void> {
        return new Promise(resolve => {
            const eventId = Symbol();
            const onMoveend = (event: any) => {
                if (event.eventId !== eventId) {
                    return;
                }
                map.off('moveend', onMoveend);
                resolve();
            };
            map.on('moveend', onMoveend);
            map.panTo(coordinates, { duration: 1000, ...options }, { eventId });
        });
    }

    export function fitBounds(bounds: LngLatBounds | LngLatBoundsLike, options?: FitBoundsOptions): Promise<void> {
        return new Promise(resolve => {
            const eventId = Symbol();
            const onMoveend = (event: any) => {
                if (event.eventId !== eventId) {
                    return;
                }
                map.off('moveend', onMoveend);
                resolve();
            };
            map.on('moveend', onMoveend);
            map.fitBounds(bounds, { duration: 1000, maxZoom: 20, ...options }, { eventId });
        });
    }

    function toggleFollowMode(map: Mapbox, followMode: boolean): void {
        if (!map) {
            return;
        }
        if (followMode) {
            enableFollowMode();
        } else {
            disableFollowMode();
        }
    }

    function enableFollowMode(): void {
        if (followModeEnabled) {
            return;
        }
        followModeEnabled = true;
        map.scrollZoom.disable();
        map.dragPan.disable();
        map.touchPitch.disable();
        map.touchZoomRotate.disable();
        unsubFollowingData = followingData.subscribe(follow);
    }

    function disableFollowMode(): void {
        if (!followModeEnabled) {
            return; 
        }
        followModeEnabled = false;
        map.scrollZoom.enable();
        map.dragPan.enable();
        map.touchPitch.enable();
        map.touchZoomRotate.enable();
        unsubFollowingData?.();

        map.flyTo({
            center: $playerPosition,
            pitch: 0,
            bearing: 0,
            duration: 1000
        });
    }

    function follow([playerPosition, target, userBearing]: [CoordinatesDto, TargetDto, number]): void {
        if (!map || !playerPosition) {
            return;
        }

        const bearing = target
            ? calcBearing(playerPosition, target.position) - userBearing
            : 30;

        map.flyTo({
            center: playerPosition,
            pitch: 65,
            bearing,
            duration: 1000,
            zoom: 18
        });
    }

</script>

<div class="map-view">
    <div class="map-container" bind:this={mapContainer}></div>
    {#if mapReady}
        <slot />
    {/if}
</div>


<style lang="scss">

    .map-view {
        height: 100%;
    }

    .map-container {
        height: 100%;
    }

</style>
